/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @author Jiri Moc <jiri.moc@outlook.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { Form } from "../shared/FormModel/FormModel";
import { SectionGroup } from "../shared/FormModel/elements/FormSectionGroup";
import { Section } from "../shared/FormModel/elements/FormSection";
import { Row } from "../shared/FormModel/elements/FormRow";
import { FieldText, FormFieldText } from "../shared/FormModel/elements/FormFieldText";
import { FieldSelect } from "../shared/FormModel/elements/FormFieldSelect";
import { FieldLabel } from "../shared/FormModel/elements/FormFieldLabel";
import { RowList, FormRowList } from "../shared/FormModel/elements/FormRowList";
import { HTMLContent } from "../shared/FormModel/elements/FormHTMLContent";
import { parseNumber, formatNumber, printNumberFormatter } from "../shared/formatting";
import { HiddenExpression, FormHiddenExpression } from "../shared/FormModel/elements/FormHiddenExpression";

export const form_cenovaNabidkaObecna = Form({
	id: "cenova-nabidka-obecna",
	version: "2020-07-r1",
	title: "Cenová nabídka - obecná",
	icon: "table-column-plus-after",
	showInMenu: true
}, [
	SectionGroup({}, [
		Section({
			id: "detail-zakaznika",
			title: "Detail zákazníka"
		}, [
			Row({}, [
				FieldText({
					width: "1/2",
					id: "zakaznik-jmeno",
					label: "Jméno a příjmení",
					placeholder: "Jan Novák"
				}),
				FieldText({
					width: "1/2",
					id: "zakaznik-adresa",
					label: "Adresa",
					placeholder: "Třešňová 23, Mladé 380 01"
				})
			]),
			Row({}, [
				FieldText({
					width: "1/2",
					id: "zakaznik-kontakt",
					label: "Kontakt",
					placeholder: "jan.novak@email.cz, + 420 123 456 789"
				}),
				FieldText({
					width: "1/2",
					id: "datum-vytvoreno",
					label: "Vytvořeno dne",
					placeholder: "1.1. 2020"
				})
			])
		]),
	]),
	SectionGroup({}, [
		Section({
			id: "provedeni-sestavy-nabytku",
			title: "Provedení sestavy nábytku"
		}, [
			Row({}, [
				FieldText({
					width: "1/2",
					id: "provedeni-uchytky",
					label: "Úchytky",
					placeholder: "---"
				}),
				FieldText({
					width: "1/2",
					id: "provedeni-sokl",
					label: "Sokl",
					placeholder: "---"
				})
			]),
			Row({}, [
				FieldText({
					width: "1/2",
					id: "provedeni-ramecek",
					label: "Rámeček",
					placeholder: "---"
				}),
				FieldText({
					width: "1/2",
					id: "provedeni-sklo",
					label: "Sklo",
					placeholder: "---"
				})
			]),
			Row({}, [
				FieldText({
					width: "1/2",
					id: "dvere",
					label: "Dveře",
					placeholder: "---"
				}),
				FieldText({
					width: "1/2",
					id: "korpus",
					label: "Korpus",
					placeholder: "---"
				})
			]),
			Row({}, [
				FieldText({
					width: "1",
					id: "dalsi-specifikace",
					label: "Další specifikace",
					placeholder: "---"
				})
			])
		]),
	]),
	SectionGroup({}, [
		Section({
			id: "kovani",
			title: "Kování"
		}, [
			Row({}, [
				FieldText({
					width: "1/3",
					id: "kovani-zavesy",
					label: "Závěsy",
					placeholder: "---"
				}),
				FieldText({
					width: "1/3",
					id: "kovani-vysuvy",
					label: "Výsuvy",
					placeholder: "---"
				}),
				FieldText({
					width: "1/3",
					id: "kovani-vyklopy",
					label: "Výklopy",
					placeholder: "---"
				})
			])
		]),
	]),
	SectionGroup({
		spaceBefore: true
	}, [
		Section({
			id: "list-skrinek",
			title: "Položky nabídky"
		}, [
			RowList({}, [
				FieldText({
					id: "nazev",
					label: "Položka + popis",
					placeholder: "---"
				}),
				FieldSelect({
					width: "1/8",
					id: "sazba-dph",
					label: "Sazba DPH",
					options: [{
						value: "21",
						label: "21%"
					}, {
						value: "15",
						label: "15%"
					}]
				}),
				FieldText({
					width: "1/6",
					id: "cena",
					tag: "polozka-cena",
					label: "Cena vč. DPH",
					placeholder: "---",
					pattern: /^[0-9 ]+((,|\.)[0-9]+)?$/,
					align: "right",
					labelAlign: "right",
					suffix: "Kč",
					printFormatter: printNumberFormatter(2)
				})
			]),
			Row({
				colorSchema: "heading"
			}, [
				FieldLabel({
					label: "Celková cena za položky vč. DPH",
					style: "summary"
				}),
				HiddenExpression({
					tag: "polozky-celkem",
					value: (model) => {
						let total = 0;
						const list = model.getElementByTag<FormFieldText>("polozka-cena");

						for (let i = 0; i < list.length; i++) {
							total += parseNumber(list[i].getState().value, 0);
						}

						return total;
					}
				}),
				FieldLabel({
					width: "1/6",
					value: (model) => {
						const total = model.getElementByTag<FormHiddenExpression>("polozky-celkem")[0].getState().value;
						return formatNumber(total, 2);
					},
					suffix: "Kč",
					align: "right",
					style: "summary"
				})
			]),
			Row({
				print: false
			}, [
				FieldText({
					id: "nazev-slevy",
					tag: "polozky-sleva-nazev",
					label: "Název slevy",
					value: "Vaše sleva"
				}),
				FieldText({
					width: "1/6",
					id: "sleva",
					tag: "polozky-sleva",
					label: "Sleva",
					pattern: /^[0-9 ]+((,|\.)[0-9]+)?$/,
					align: "right",
					labelAlign: "right",
					suffix: "%",
					value: "0"
				}),
				FieldLabel({
					width: "1/6",
					label: "Výše slevy",
					value: (model) => {
						const total = model.getElementByTag<FormHiddenExpression>("polozky-celkem")[0].getState().value;
						const discount = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value);

						const discountAmount = -(total * (discount / 100));

						return formatNumber(discountAmount, 2);
					},
					align: "right",
					labelAlign: "right",
					suffix: "Kč"
				}),
				HiddenExpression({
					tag: "polozky-celkem-po-sleve",
					value: (model) => {
						const total = model.getElementByTag<FormHiddenExpression>("polozky-celkem")[0].getState().value;
						const discount = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value);

						const afterDiscount = total - (total * (discount / 100));

						return afterDiscount;
					}
				}),
			]),
			Row({
				colorSchema: "highlight",
				display: (model) => {
					const discount = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value, 0);
					return discount > 0;
				}
			}, [
				FieldLabel({
					label: (model) => {
						const discountName = model.getElementByTag<FormFieldText>("polozky-sleva-nazev")[0].getState().value;
						const discount = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value);

						return "Cena po slevě vč. DPH (" + (discountName || "") + " " + formatNumber(discount, 0) + "%)";
					},
					style: "summary"
				}),
				FieldLabel({
					width: "1/6",
					align: "right",
					value: (model) => {
						const total = model.getElementByTag<FormHiddenExpression>("polozky-celkem")[0].getState().value;
						const discount = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value);

						const afterDiscount = total - (total * (discount / 100));

						return formatNumber(afterDiscount, 2);
					},
					style: "summary",
					suffix: "Kč"
				})
			])
		]),
	]),
	SectionGroup({
		spaceBefore: true
	}, [
		Section({},
			[
				Row({}, [
					FieldText({
						id: "montaz-a-doprava-v-cene",
						value: "Montáž a doprava v ceně."
					}),
				]),
				Row({
					colorSchema: "heading",
					display: (model) => {
						const discount1 = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value, 0);

						return discount1 === 0;
					}
				}, [
					FieldLabel({
						label: "Celková cena vč. DPH",
						style: "summary"
					}),
					FieldLabel({
						width: "1/6",
						align: "right",
						value: (model) => {
							const total1 = model.getElementByTag<FormHiddenExpression>("polozky-celkem-po-sleve")[0].getState().value;

							const sum = total1;

							return formatNumber(sum, 2);
						},
						style: "summary",
						suffix: "Kč"
					})
				]),
				Row({
					colorSchema: "highlight",
					display: (model) => {
						const discount1 = parseNumber(model.getElementByTag<FormHiddenExpression>("polozky-sleva")[0].getState().value, 0);

						return discount1 > 0;
					}
				}, [
					FieldLabel({
						label: "Celková cena po slevě vč. DPH",
						style: "summary"
					}),
					FieldLabel({
						width: "1/6",
						align: "right",
						value: (model) => {
							const total1 = model.getElementByTag<FormHiddenExpression>("polozky-celkem-po-sleve")[0].getState().value;

							const sum = total1;

							return formatNumber(sum, 2);
						},
						style: "summary",
						suffix: "Kč"
					})
				])
			])
	])
]);